import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, Paper, Table,
  TableBody, TableCell, TableHead, TablePagination, TableRow, TextField,
} from "@material-ui/core";
import { useNotify } from "react-admin";
import { cloError, cloGet, cloPost, ENDPOINTS } from "./api";
import { useRetailers } from "./useRetailers";
import VersionEditor from "./VersionEditor";
import { DEFAULT_VERSION_FORM, toVersionBody, VersionForm } from "./versionForm";
import CampaignDetail from "./CampaignDetail";

const STATUSES = ["draft", "awaiting_approval", "approved", "live", "paused", "ended"];

export const statusChip = (status: string) => (
  <Chip size="small" label={status} color={status === "live" ? "primary" : status === "awaiting_approval" ? "secondary" : "default"} />
);

const CampaignsTab: React.FC = () => {
  const notify = useNotify();
  const retailers = useRetailers();
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [brandId, setBrandId] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [header, setHeader] = useState({ brandId: "", name: "", visaOfferId: "", partnerCampaignRef: "" });
  const [version, setVersion] = useState<VersionForm>(DEFAULT_VERSION_FORM);
  const [openId, setOpenId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await cloGet(ENDPOINTS.CLO_CAMPAIGNS, {
        page: page + 1, pageSize, brandId: brandId || undefined, status: status || undefined, search: search || undefined,
      });
      setRows(res.data || []);
      setTotal(res.pagination?.total || 0);
    } catch (e) {
      notify(cloError(e), "warning");
    }
  }, [page, pageSize, brandId, status, search, notify]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const create = async () => {
    try {
      const res = await cloPost(ENDPOINTS.CLO_CAMPAIGNS, {
        brandId: Number(header.brandId),
        name: header.name,
        offers: header.visaOfferId.trim() ? [{ network: "visa", offerId: header.visaOfferId.trim() }] : [],
        partnerCampaignRef: header.partnerCampaignRef.trim() || null,
        version: toVersionBody(version),
      });
      notify("Campaign created as a draft", "info");
      setCreating(false);
      setOpenId(res.data.id);
      load();
    } catch (e) {
      notify(cloError(e), "warning");
    }
  };

  return (
    <Paper>
      <Box p={2} display="flex" flexWrap="wrap" alignItems="center" style={{ gap: 12 }}>
        <TextField select size="small" variant="outlined" label="Retailer" value={brandId} onChange={(e) => setBrandId(e.target.value)} style={{ minWidth: 180 }}>
          <MenuItem value="">All</MenuItem>
          {retailers.map((r) => <MenuItem key={r.brand_id} value={String(r.brand_id)}>{r.display_name}</MenuItem>)}
        </TextField>
        <TextField select size="small" variant="outlined" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} style={{ minWidth: 180 }}>
          <MenuItem value="">All</MenuItem>
          {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        <TextField size="small" variant="outlined" label="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
        <Box flexGrow={1} />
        <Button variant="contained" color="primary" onClick={() => { setVersion(DEFAULT_VERSION_FORM); setCreating(true); }}>New campaign</Button>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Campaign</TableCell>
            <TableCell>Retailer</TableCell>
            <TableCell>Dates</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Live version</TableCell>
            <TableCell>Waiting for approval</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} hover>
              <TableCell>{r.id}</TableCell>
              <TableCell>{r.name}</TableCell>
              <TableCell>{r.retailer_name}</TableCell>
              <TableCell>{r.start_date ? `${String(r.start_date).slice(0, 10)} → ${String(r.end_date).slice(0, 10)}` : "—"}</TableCell>
              <TableCell>{statusChip(r.status)}</TableCell>
              <TableCell>{r.current_version ? `v${r.current_version}` : "—"}</TableCell>
              <TableCell>
                {r.awaiting_versions && String(r.awaiting_versions).split(",").map((v: string) => (
                  <Chip key={v} size="small" color="secondary" label={`v${v} waiting`} style={{ marginRight: 4 }} />
                ))}
                {Number(r.awaiting_resume) > 0 && <Chip size="small" color="secondary" label="Resume waiting" />}
                {!r.awaiting_versions && Number(r.awaiting_resume) === 0 && "—"}
              </TableCell>
              <TableCell><Button size="small" onClick={() => setOpenId(r.id)}>Open</Button></TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={8} align="center">No campaigns</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
      <TablePagination
        component="div" count={total} page={page} rowsPerPage={pageSize}
        onChangePage={(_, p) => setPage(p)}
        onChangeRowsPerPage={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
      />

      <Dialog disableBackdropClick open={creating} onClose={() => setCreating(false)} maxWidth="lg" fullWidth>
        <DialogTitle>New campaign</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField select fullWidth label="Retailer" value={header.brandId} onChange={(e) => setHeader({ ...header, brandId: e.target.value })}>
                {retailers.map((r) => <MenuItem key={r.brand_id} value={String(r.brand_id)}>{r.display_name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth label="Name" value={header.name} onChange={(e) => setHeader({ ...header, name: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth label="Visa offer ID (optional)" value={header.visaOfferId} onChange={(e) => setHeader({ ...header, visaOfferId: e.target.value })} helperText="From Visa once the offer is registered; can be added later" />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth label="Partner campaign ID (optional)" value={header.partnerCampaignRef} onChange={(e) => setHeader({ ...header, partnerCampaignRef: e.target.value })} helperText="The retailer's own campaign ID, e.g. eBay Partner Network" inputProps={{ maxLength: 64 }} />
            </Grid>
          </Grid>
          <Box mt={3}>
            <VersionEditor value={version} onChange={setVersion} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreating(false)}>Cancel</Button>
          <Button color="primary" variant="contained" onClick={create}>Create draft</Button>
        </DialogActions>
      </Dialog>

      {openId !== null && <CampaignDetail id={openId} onClose={() => { setOpenId(null); load(); }} />}
    </Paper>
  );
};

export default CampaignsTab;
